import { Checkbox, Label, Text } from "north.js";
import React, { useMemo } from "react";
import styled from "styled-components";

import { HardwareRequestStatus } from "src/api/types.generated";
import { formatHardwareRequestStatus } from "src/shared/utils/hardware/formatHardwareRequestStatus";

const STATUS_ORDER: HardwareRequestStatus[] = [
  HardwareRequestStatus.Waiting,
  HardwareRequestStatus.Requested,
  HardwareRequestStatus.Accepted,
  HardwareRequestStatus.Ready,
  HardwareRequestStatus.PickedUp,
  HardwareRequestStatus.Returned,
  HardwareRequestStatus.Denied,
  HardwareRequestStatus.Cancelled,
];

type StatusFilterTilesProps = {
  organizerMode?: boolean;
  statusCounts: Partial<Record<HardwareRequestStatus, number>>;
  selectedStatuses: Set<HardwareRequestStatus>;
  onToggleStatus: (status: HardwareRequestStatus) => void;
  onClearAllStatuses: () => void;
  onSelectAllStatuses: () => void;
  hideLottery?: boolean;
  onHideLotteryChange?: (checked: boolean) => void;
  children?: React.ReactNode;
};

const StatusFilterTiles: React.FC<StatusFilterTilesProps> = ({
  organizerMode = false,
  statusCounts,
  selectedStatuses,
  onToggleStatus,
  onClearAllStatuses,
  onSelectAllStatuses,
  hideLottery = false,
  onHideLotteryChange,
  children,
}) => {
  const visibleStatuses = useMemo(
    () =>
      STATUS_ORDER.filter(
        (status) => organizerMode || status !== HardwareRequestStatus.Waiting
      ),
    [organizerMode]
  );

  return (
    <StatusCountsSection>
      <SectionHeading>
        <Text type="h3">Request Status Overview</Text>
      </SectionHeading>
      <StatusCountsGrid>
        {visibleStatuses.map((status) => (
          <StatusCountCard
            key={status}
            type="button"
            isActive={selectedStatuses.has(status)}
            onClick={() => onToggleStatus(status)}
          >
            <StatusCountNumber>{statusCounts[status] ?? 0}</StatusCountNumber>
            <StatusCountLabel>
              {formatHardwareRequestStatus(status)}
            </StatusCountLabel>
          </StatusCountCard>
        ))}
      </StatusCountsGrid>

      <CenteredControls>
        <CenteredControlsRow>
          <LabelButton onClick={onClearAllStatuses} type="button">
            <Label
              color={
                selectedStatuses.size === 0 ? "primary" : "secondary-light"
              }
            >
              Clear All
            </Label>
          </LabelButton>
          <LabelButton onClick={onSelectAllStatuses} type="button">
            <Label
              color={
                selectedStatuses.size === visibleStatuses.length
                  ? "primary"
                  : "secondary-light"
              }
            >
              Select All
            </Label>
          </LabelButton>
        </CenteredControlsRow>
        {organizerMode && onHideLotteryChange && (
          <Checkbox
            checked={hideLottery}
            onChange={(e) => onHideLotteryChange(e.target.checked)}
            label="Hide Special Items"
          />
        )}
      </CenteredControls>
      {children}
    </StatusCountsSection>
  );
};

const StatusCountsSection = styled.div`
  margin-bottom: 32px;
  padding: 20px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.textLight};
`;

const SectionHeading = styled.div`
  margin-bottom: 10px;
`;

const CenteredControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

const CenteredControlsRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const LabelButton = styled.button`
  border-radius: 9999px;
`;

const StatusCountsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
`;

const StatusCountCard = styled.button<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 16px;
  background: ${({ isActive, theme }) =>
    isActive ? theme.globalConstants.color.brandPrimary + "1A" : "white"};
  border-radius: 8px;
  border: 1px solid
    ${({ isActive, theme }) =>
      isActive
        ? theme.globalConstants.color.brandPrimary
        : theme.globalConstants.color.textLight};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-family: inherit;
  outline: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  }
`;

const StatusCountNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.globalConstants.color.brandPrimary};
  margin-bottom: 4px;
`;

const StatusCountLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  text-align: center;
  line-height: 1.2;
`;

export default StatusFilterTiles;
